/**
 * MASTER PRICING CONFIGURATION
 * =============================
 * This is the SINGLE SOURCE OF TRUTH for all pricing in the application.
 * 
 * When adding or modifying prices:
 * 1. Update this file FIRST
 * 2. Sync edge functions (calculate-video-cost, generate-video) with these values
 * 3. The admin dashboard will automatically reflect changes
 * 
 * Credit System: 1 credit = $0.0002 (scaled 100x for user-friendly numbers)
 * Formula: credits = Math.ceil(replicateCost / (1 - margin) / 0.02) * 100
 */

// ============================================================================
// CORE PRICING CONSTANTS
// ============================================================================

export const PRICING_CONSTANTS = {
  /** Value of 1 credit in USD (scaled system: 1 credit = $0.0002) */
  creditValueUsd: 0.0002,
  
  /** Target margin for budget/standard models (67% = 3x markup) */
  targetMargin: 0.67,
  
  /** Target margin for premium models (50% = 2x markup) */
  premiumMargin: 0.50,
  
  /** Minimum credit charge for any generation */
  minCredits: 500,
};

// ============================================================================
// VIDEO MODEL PRICING
// ============================================================================

export type VideoTier = 'budget' | 'medium' | 'high' | 'animate';
export type PricingType = 'per-second' | 'lookup' | 'fixed' | 'formula';

export interface VideoModelPricing {
  id: string;
  displayName: string;
  tier: VideoTier;
  pricingType: PricingType;
  supportsAudio: boolean | 'optional';
  
  // Per-second pricing (for per-second and fixed types)
  perSecond?: number;
  perSecondWithAudio?: number;
  
  // Fixed pricing (for wan-super)
  fixedCost?: number;
  fixedCredits?: number;
  
  // Lookup table pricing (for lookup type)
  lookupTable?: Record<string, number>;
  
  // Formula pricing (for wan-animate)
  formula?: {
    base: number;
    per5s: number;
    hd1080pMultiplier: number;
  };
  
  // Supported configurations
  durations: number[];
  qualities: string[];
  aspectRatios: string[];
  motionModes?: string[];
  
  // Use premium margin instead of standard
  usePremiumMargin?: boolean;
  
  // Custom margin override (0-1, e.g., 0.60 for 60%)
  customMargin?: number;
}

export const VIDEO_MODEL_PRICING: VideoModelPricing[] = [
  // === BUDGET TIER ===
  {
    id: 'seedance-1.5-pro',
    displayName: 'Seedance 1.5 Pro',
    tier: 'budget',
    pricingType: 'per-second',
    supportsAudio: 'optional',
    perSecond: 0.026, // Without audio
    perSecondWithAudio: 0.052, // With audio
    durations: [5, 10],  // Fixed: only 5 and 10 seconds verified to work
    qualities: ['720p'],
    aspectRatios: ['16:9', '9:16', '1:1'],
  },
  {
    id: 'seedance-1-pro-fast',
    displayName: 'Seedance 1 Pro Fast',
    tier: 'budget',
    pricingType: 'lookup',
    supportsAudio: false,
    lookupTable: {
      '5s-480p': 0.075,   // $0.015/sec * 5s
      '8s-480p': 0.12,    // $0.015/sec * 8s
      '10s-480p': 0.15,   // $0.015/sec * 10s
      '12s-480p': 0.18,   // $0.015/sec * 12s
      '5s-720p': 0.125,   // $0.025/sec * 5s
      '8s-720p': 0.20,    // $0.025/sec * 8s
      '10s-720p': 0.25,   // $0.025/sec * 10s
      '12s-720p': 0.30,   // $0.025/sec * 12s
      '5s-1080p': 0.30,   // $0.06/sec * 5s
      '8s-1080p': 0.48,   // $0.06/sec * 8s
      '10s-1080p': 0.60,  // $0.06/sec * 10s
      '12s-1080p': 0.72,  // $0.06/sec * 12s
    },
    durations: [5, 8, 10, 12],
    qualities: ['480p', '720p', '1080p'],
    aspectRatios: ['16:9', '9:16', '1:1'],
  },
  {
    id: 'hailuo-02',
    displayName: 'Hailuo 02',
    tier: 'budget',
    pricingType: 'lookup',
    supportsAudio: false,
    customMargin: 0.60, // 60% margin
    lookupTable: {
      '6s-512p': 0.10,
      '10s-512p': 0.15,
      '6s-768p': 0.27,
      '10s-768p': 0.45,
      '6s-1080p': 0.48,
    },
    durations: [6, 10],
    qualities: ['512p', '768p', '1080p'],
    aspectRatios: ['16:9', '9:16', '1:1'],
  },
  {
    id: 'wan-super',
    displayName: 'Wan Super',
    tier: 'budget',
    pricingType: 'fixed',
    supportsAudio: false,
    perSecond: 0.01, // $0.05 / 5s = $0.01/s (for calculation purposes)
    fixedCost: 0.05, // Fixed $0.05 per generation
    fixedCredits: 500, // Fixed 500 credits (50% margin)
    durations: [5],
    qualities: ['480p'], // 720p excluded (doubles cost)
    aspectRatios: ['16:9', '9:16', '1:1'],
  },

  // === MEDIUM TIER ===
  {
    id: 'pixverse-v5',
    displayName: 'PixVerse v5',
    tier: 'medium',
    pricingType: 'lookup',
    supportsAudio: false,
    customMargin: 0.60, // 60% margin = 150% markup (popular model)
    lookupTable: {
      // 360p pricing
      '5s-360p-normal': 0.30,
      '5s-360p-smooth': 0.60,
      '8s-360p-normal': 0.60,
      // 540p pricing
      '5s-540p-normal': 0.30,
      '5s-540p-smooth': 0.60,
      '8s-540p-normal': 0.60,
      // 720p pricing
      '5s-720p-normal': 0.40,
      '5s-720p-smooth': 0.80,
      '8s-720p-normal': 0.80,
      // 1080p pricing
      '5s-1080p-normal': 0.80,
      '5s-1080p-smooth': 1.60,
      '8s-1080p-normal': 1.60,
      '8s-1080p-smooth': 3.20,
    },
    durations: [5, 8],
    qualities: ['360p', '540p', '720p', '1080p'],
    aspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4'],
    motionModes: ['normal', 'smooth'],
  },
  {
    id: 'kling-v2.6',
    displayName: 'Kling 2.6',
    tier: 'medium',
    pricingType: 'per-second',
    supportsAudio: 'optional',
    customMargin: 0.60, // 60% margin = 150% markup (popular model)
    perSecond: 0.07,
    perSecondWithAudio: 0.14,
    durations: [5, 8, 10],
    qualities: ['720p', '1080p'],
    aspectRatios: ['16:9', '9:16', '1:1'],
  },

  // === HIGH TIER ===
  {
    id: 'sora-2',
    displayName: 'Sora 2',
    tier: 'high',
    pricingType: 'per-second',
    supportsAudio: true, // Always has audio
    perSecond: 0.10,
    durations: [4, 8, 12],
    qualities: ['720p', '1080p'], // Fixed quality, price same
    aspectRatios: ['16:9', '9:16'], // Landscape or portrait only
    customMargin: 0.40, // 40% margin = 67% markup (premium)
  },
  {
    id: 'sora-2-pro-standard',
    displayName: 'Sora 2 Pro (Standard)',
    tier: 'high',
    pricingType: 'per-second',
    supportsAudio: true, // Always has audio
    perSecond: 0.30,
    durations: [4, 8, 12],
    qualities: ['720p', '1080p'],
    aspectRatios: ['16:9', '9:16'],
    customMargin: 0.40, // 40% margin = 67% markup (premium)
  },
  {
    id: 'sora-2-pro-high',
    displayName: 'Sora 2 Pro (High)',
    tier: 'high',
    pricingType: 'per-second',
    supportsAudio: true, // Always has audio
    perSecond: 0.50,
    durations: [4, 8, 12],
    qualities: ['720p', '1080p'],
    aspectRatios: ['16:9', '9:16'],
    customMargin: 0.40, // 40% margin = 67% markup (premium)
  },
  {
    id: 'veo-3.1-fast',
    displayName: 'Veo 3.1 Fast',
    tier: 'high',
    pricingType: 'per-second',
    supportsAudio: 'optional',
    perSecond: 0.10,
    perSecondWithAudio: 0.15, // $0.15/sec with audio
    durations: [4, 6, 8],
    qualities: ['720p'], // Resolution-independent pricing
    aspectRatios: ['16:9', '9:16'],
    customMargin: 0.40, // 40% margin = 67% markup (premium)
  },
  {
    id: 'veo-3.1',
    displayName: 'Veo 3.1',
    tier: 'high',
    pricingType: 'per-second',
    supportsAudio: 'optional',
    perSecond: 0.20,
    perSecondWithAudio: 0.40, // $0.40/sec with audio
    durations: [4, 6, 8],
    qualities: ['720p'], // Resolution-independent pricing
    aspectRatios: ['16:9', '9:16', '1:1'],
    customMargin: 0.40, // 40% margin = 67% markup (premium)
  },

  // === ANIMATE YOUR IMAGE ===
  {
    id: 'kling-motion-control',
    displayName: 'Kling Motion Control',
    tier: 'animate',
    pricingType: 'per-second',
    supportsAudio: false,
    customMargin: 0.50, // 50% margin = 100% markup
    perSecond: 0.07, // Standard mode
    perSecondWithAudio: 0.12, // Pro mode (reusing field for pro pricing)
    durations: [5, 10], // Estimated durations for cost preview
    qualities: ['standard', 'pro'], // Mode selection
    aspectRatios: ['16:9', '9:16', '1:1'],
  },
];

// ============================================================================
// IMAGE MODEL PRICING
// ============================================================================

export interface ImageModelPricing {
  id: string;
  displayName: string;
  replicateCostUsd: number;
  creditCost: number;
  marginPercent: number;
}

export const IMAGE_MODEL_PRICING: ImageModelPricing[] = [
  { id: 'flux-schnell', displayName: 'Flux Schnell', replicateCostUsd: 0.003, creditCost: 50, marginPercent: 89 },
  { id: 'hidream-l1-fast', displayName: 'HiDream L1 Fast', replicateCostUsd: 0.015, creditCost: 100, marginPercent: 87 },
  { id: 'minimax-image-01', displayName: 'MiniMax Image', replicateCostUsd: 0.01, creditCost: 150, marginPercent: 87 },
  { id: 'flux-dev', displayName: 'Black Forest Dev', replicateCostUsd: 0.025, creditCost: 250, marginPercent: 100 },
  { id: 'nano-banana', displayName: 'Nano Banana', replicateCostUsd: 0.039, creditCost: 600, marginPercent: 87 },
  { id: 'nano-banana-edit', displayName: 'Nano Banana Edit', replicateCostUsd: 0.039, creditCost: 600, marginPercent: 87 },
];

// ============================================================================
// MUSIC MODEL PRICING
// ============================================================================

export interface MusicModelPricing {
  id: string;
  displayName: string;
  replicateCostUsd: number;
  creditCost: number;
  marginPercent: number;
}

export const MUSIC_MODEL_PRICING: MusicModelPricing[] = [
  { id: 'suno', displayName: 'Suno V5', replicateCostUsd: 0.625, creditCost: 1563, marginPercent: 60 },
  { id: 'minimax', displayName: 'Minimax 1.5', replicateCostUsd: 0.03, creditCost: 450, marginPercent: 67 },
];

// ============================================================================
// SPEECH MODEL PRICING
// ============================================================================

export type SpeechTier = 'budget' | 'premium';
export type SpeechPricingType = 'flat' | 'per-char';

export interface SpeechModelPricing {
  id: string;
  displayName: string;
  tier: SpeechTier;
  pricingType: SpeechPricingType;
  replicateModel: string;
  // Flat pricing (for Kokoro)
  flatCredits?: number;
  replicateCostPerRun?: number;
  // Per-char pricing (for Minimax)
  replicateCostPer1000Chars?: number;
  creditsPerChar?: number;
  minCredits?: number;
  maxChars: number;
  marginPercent: number;
  // Supported languages (Kokoro is limited)
  languages?: string[];
}

export const SPEECH_MODEL_PRICING: SpeechModelPricing[] = [
  { 
    id: 'minimax-speech-2.6-hd', 
    displayName: 'MiniMax Speech 2.6 HD',
    tier: 'premium',
    pricingType: 'per-char',
    replicateModel: 'minimax/speech-2.6-hd',
    replicateCostPer1000Chars: 0.10,
    creditsPerChar: 0.5,
    minCredits: 150,
    maxChars: 10000,
    marginPercent: 67,
  },
  { 
    id: 'kokoro-82m', 
    displayName: 'Kokoro 82M',
    tier: 'budget',
    pricingType: 'flat',
    replicateModel: 'jaaari/kokoro-82m:f559560eb822dc509045f3921a1921234918b91739db4bf3daab2169b71c7a13',
    flatCredits: 100,
    replicateCostPerRun: 0.00023,
    maxChars: 5000,
    marginPercent: 98,
    languages: ['en', 'fr', 'hi', 'it', 'ja', 'zh'],
  },
];

/**
 * Calculate speech cost based on model and character count
 */
export function calculateSpeechCredits(charCount: number, modelId: string = 'minimax-speech-2.6-hd'): number {
  const pricing = SPEECH_MODEL_PRICING.find(m => m.id === modelId) || SPEECH_MODEL_PRICING[1];
  
  if (pricing.pricingType === 'flat') {
    return pricing.flatCredits || 100;
  }
  
  return Math.max(pricing.minCredits || 150, Math.ceil(charCount * (pricing.creditsPerChar || 0.5)));
}

/**
 * Get speech model by ID
 */
export function getSpeechModel(modelId: string): SpeechModelPricing | undefined {
  return SPEECH_MODEL_PRICING.find(m => m.id === modelId);
}

// ============================================================================
// CHAT MODEL PRICING (Hybrid Token-Based)
// ============================================================================

export interface ChatModelPricing {
  id: string;
  displayName: string;
  /** Base credits charged per message */
  baseCredits: number;
  /** Additional credits per 1,000 input tokens */
  inputRatePerK: number;
  /** Additional credits per 1,000 output tokens */
  outputRatePerK: number;
  /** Maximum output tokens allowed for this model */
  maxOutputTokens: number;
  /** Anthropic API input cost per 1M tokens (for reference) */
  inputCostPerMToken: number;
  /** Anthropic API output cost per 1M tokens (for reference) */
  outputCostPerMToken: number;
}

export const CHAT_MODEL_PRICING: ChatModelPricing[] = [
  { 
    id: 'claude-3-5-haiku-20241022', 
    displayName: 'Haiku 3.5 - Fast',
    baseCredits: 50,
    inputRatePerK: 15,      // $0.80/1M = $0.0008/1K -> 15 credits/1K for 67% margin
    outputRatePerK: 75,     // $4.00/1M = $0.004/1K -> 75 credits/1K for 67% margin
    maxOutputTokens: 8192,
    inputCostPerMToken: 0.80,
    outputCostPerMToken: 4.00,
  },
  { 
    id: 'claude-sonnet-4-20250514', 
    displayName: 'Sonnet 4 - Balanced',
    baseCredits: 100,
    inputRatePerK: 45,      // $3.00/1M = $0.003/1K -> 45 credits/1K for 67% margin
    outputRatePerK: 230,    // $15.00/1M = $0.015/1K -> 230 credits/1K for 67% margin
    maxOutputTokens: 16384,
    inputCostPerMToken: 3.00,
    outputCostPerMToken: 15.00,
  },
  { 
    id: 'claude-sonnet-4-5', 
    displayName: 'Sonnet 4.5 - Best',
    baseCredits: 100,
    inputRatePerK: 45,      // $3.00/1M = $0.003/1K -> 45 credits/1K for 67% margin
    outputRatePerK: 230,    // $15.00/1M = $0.015/1K -> 230 credits/1K for 67% margin
    maxOutputTokens: 32000,
    inputCostPerMToken: 3.00,
    outputCostPerMToken: 15.00,
  },
  { 
    id: 'claude-opus-4-5-20251101', 
    displayName: 'Opus 4.5 - Premium',
    baseCredits: 200,
    inputRatePerK: 230,     // $15.00/1M = $0.015/1K -> 230 credits/1K for 67% margin
    outputRatePerK: 1140,   // $75.00/1M = $0.075/1K -> 1140 credits/1K for 67% margin
    maxOutputTokens: 16000,
    inputCostPerMToken: 15.00,
    outputCostPerMToken: 75.00,
  },
];

/**
 * Calculate chat credit cost based on input AND output tokens
 */
export function calculateChatCredits(inputTokens: number, outputTokens: number, modelId: string): number {
  const pricing = CHAT_MODEL_PRICING.find(m => m.id === modelId) || CHAT_MODEL_PRICING[1];
  const inputCost = Math.ceil((inputTokens / 1000) * pricing.inputRatePerK);
  const outputCost = Math.ceil((outputTokens / 1000) * pricing.outputRatePerK);
  return Math.max(pricing.baseCredits, inputCost + outputCost);
}

/**
 * Get minimum credits required to use a chat model (base credits)
 */
export function getMinChatCredits(modelId: string): number {
  const pricing = CHAT_MODEL_PRICING.find(m => m.id === modelId) || CHAT_MODEL_PRICING[1];
  return pricing.baseCredits;
}

/**
 * Get chat model by ID
 */
export function getChatModel(modelId: string): ChatModelPricing | undefined {
  return CHAT_MODEL_PRICING.find(m => m.id === modelId);
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Calculate credits from replicate cost using target margin
 */
export function calculateCreditsFromCost(
  replicateCostUsd: number, 
  usePremiumMargin: boolean = false,
  customMargin?: number
): number {
  const margin = customMargin ?? (usePremiumMargin ? PRICING_CONSTANTS.premiumMargin : PRICING_CONSTANTS.targetMargin);
  const userPaysUsd = replicateCostUsd / (1 - margin);
  const credits = Math.ceil(userPaysUsd / PRICING_CONSTANTS.creditValueUsd);
  return Math.max(credits, PRICING_CONSTANTS.minCredits);
}

/**
 * Calculate margin percentage from replicate cost and credit cost
 */
export function calculateMargin(replicateCostUsd: number, creditCost: number): number {
  const userPaysUsd = creditCost * PRICING_CONSTANTS.creditValueUsd;
  const margin = 1 - (replicateCostUsd / userPaysUsd);
  return Math.round(margin * 100);
}

/**
 * Get video model cost for a specific configuration
 */
export function getVideoModelCost(
  modelId: string,
  duration: number,
  quality: string,
  motionMode: string = 'normal',
  enableAudio: boolean = false
): { replicateCostUsd: number; creditCost: number; marginPercent: number } | null {
  const model = VIDEO_MODEL_PRICING.find(m => m.id === modelId);
  if (!model) return null;
  
  let replicateCostUsd: number;
  
  switch (model.pricingType) {
    case 'per-second':
      replicateCostUsd = enableAudio && model.perSecondWithAudio 
        ? model.perSecondWithAudio * duration 
        : (model.perSecond || 0) * duration;
      break;
      
    case 'fixed':
      // Fixed pricing models like wan-super
      if (model.fixedCost !== undefined && model.fixedCredits !== undefined) {
        return { 
          replicateCostUsd: model.fixedCost, 
          creditCost: model.fixedCredits, 
          marginPercent: 50 
        };
      }
      replicateCostUsd = enableAudio && model.perSecondWithAudio 
        ? model.perSecondWithAudio * duration 
        : (model.perSecond || 0) * duration;
      break;
      
    case 'lookup':
      if (!model.lookupTable) return null;
      const key = model.motionModes 
        ? `${duration}s-${quality}-${motionMode}`
        : `${duration}s-${quality}`;
      replicateCostUsd = model.lookupTable[key];
      if (replicateCostUsd === undefined) return null;
      break;
      
    case 'formula':
      if (!model.formula) return null;
      const chunks = Math.ceil(duration / 5);
      replicateCostUsd = model.formula.base + (chunks - 1) * model.formula.per5s;
      if (quality === '1080p') {
        replicateCostUsd *= model.formula.hd1080pMultiplier;
      }
      break;
      
    default:
      return null;
  }
  
  const creditCost = calculateCreditsFromCost(replicateCostUsd, model.usePremiumMargin, model.customMargin);
  const marginPercent = calculateMargin(replicateCostUsd, creditCost);
  
  return { replicateCostUsd, creditCost, marginPercent };
}

/**
 * Generate all possible video model configurations with costs
 */
export interface VideoConfigCost {
  modelId: string;
  modelName: string;
  tier: VideoTier;
  duration: number;
  quality: string;
  motionMode: string;
  enableAudio: boolean;
  replicateCostUsd: number;
  creditCost: number;
  marginPercent: number;
  supportsAudio: boolean | 'optional';
}

export function getAllVideoModelCosts(): VideoConfigCost[] {
  const costs: VideoConfigCost[] = [];
  
  for (const model of VIDEO_MODEL_PRICING) {
    const motionModes = model.motionModes || ['normal'];
    const audioOptions = model.supportsAudio === 'optional' ? [false, true] : 
                         model.supportsAudio === true ? [true] : [false];
    
    for (const duration of model.durations) {
      for (const quality of model.qualities) {
        // For Hailuo, skip 1080p at 10s
        if (model.id === 'hailuo-02' && quality === '1080p' && duration === 10) continue;
        
        for (const motionMode of motionModes) {
          for (const enableAudio of audioOptions) {
            const cost = getVideoModelCost(model.id, duration, quality, motionMode, enableAudio);
            if (cost) {
              costs.push({
                modelId: model.id,
                modelName: model.displayName,
                tier: model.tier,
                duration,
                quality,
                motionMode,
                enableAudio,
                supportsAudio: model.supportsAudio,
                ...cost,
              });
            }
          }
        }
      }
    }
  }
  
  return costs;
}

// ============================================================================
// PROVIDER RESERVE ESTIMATION
// ============================================================================

export type Provider = 'replicate' | 'suno' | 'anthropic';

export interface SpendingDistribution {
  video: number;
  image: number;
  music_suno: number;
  music_minimax: number;
  speech: number;
  chat: number;
  other: number;
}

export interface ProviderReserves {
  replicate: number;
  suno: number;
  anthropic: number;
  total: number;
}

/**
 * Average provider cost per credit for each category
 * Based on typical model mix and margins from pricing config
 */
const PROVIDER_COST_PER_CREDIT: Record<string, { provider: Provider; costPerCredit: number }> = {
  // Video: Mix of models, average ~60% margin -> provider gets ~40% of credit value
  video: { provider: 'replicate', costPerCredit: 0.00008 },
  // Image: Flux models, ~87% margin -> provider gets ~13% of credit value
  image: { provider: 'replicate', costPerCredit: 0.000026 },
  // Music Suno: 60% margin -> provider gets ~40% of credit value
  music_suno: { provider: 'suno', costPerCredit: 0.00008 },
  // Music Minimax: 67% margin -> provider gets ~33% of credit value
  music_minimax: { provider: 'replicate', costPerCredit: 0.000066 },
  // Speech: Mix of Kokoro (98% margin) and Minimax (67% margin)
  speech: { provider: 'replicate', costPerCredit: 0.00004 },
  // Chat: Claude models, variable margins, ~$0.015/1K output tokens avg
  chat: { provider: 'anthropic', costPerCredit: 0.00006 },
  // Other/unknown: assume Replicate at average margin
  other: { provider: 'replicate', costPerCredit: 0.00007 },
};

/**
 * Default spending distribution when no history is available
 * Based on typical usage patterns
 */
export const DEFAULT_SPENDING_DISTRIBUTION: SpendingDistribution = {
  video: 60,
  image: 20,
  music_suno: 8,
  music_minimax: 2,
  speech: 3,
  chat: 5,
  other: 2,
};

/**
 * Calculate provider reserves needed based on remaining credits and spending distribution
 */
export function calculateProviderReserves(
  remainingCredits: number,
  spendingDistribution?: SpendingDistribution,
  useConservative: boolean = false
): ProviderReserves {
  const dist = spendingDistribution || DEFAULT_SPENDING_DISTRIBUTION;
  
  // Calculate total for percentage calculation
  const total = Object.values(dist).reduce((sum, val) => sum + val, 0) || 1;
  
  // Initialize reserves
  const reserves: ProviderReserves = {
    replicate: 0,
    suno: 0,
    anthropic: 0,
    total: 0,
  };
  
  // For conservative estimate, use lower margins (higher provider cost)
  const conservativeMultiplier = useConservative ? 1.5 : 1.0;
  
  // Calculate reserves for each category
  for (const [category, data] of Object.entries(PROVIDER_COST_PER_CREDIT)) {
    const categorySpend = (dist[category as keyof SpendingDistribution] || 0) / total;
    const creditsForCategory = remainingCredits * categorySpend;
    const providerCost = creditsForCategory * data.costPerCredit * conservativeMultiplier;
    
    reserves[data.provider] += providerCost;
  }
  
  reserves.total = reserves.replicate + reserves.suno + reserves.anthropic;
  
  return reserves;
}

/**
 * Format USD amount for display
 */
export function formatUsdAmount(amount: number): string {
  if (amount < 0.01) return '< $0.01';
  if (amount < 1) return `$${amount.toFixed(2)}`;
  if (amount < 100) return `$${amount.toFixed(2)}`;
  return `$${Math.round(amount).toLocaleString()}`;
}
